/**
 * Read-only rendering of one image answer. The answer itself holds blob paths;
 * the signed ImageKit pair for each path arrives separately in answerImages, so
 * this component joins the two by path and hands the result to the same gallery
 * the damage-exception and return-inspection screens use.
 */
import React, { FC, useMemo } from 'react';
import { GalleryImages } from '@/ui/components';
import type { QuestionnaireAnswerImage } from '@/api/Questionnaire/types';

interface ReadOnlyImageAnswerProps {
  paths: string[];
  answerImages?: QuestionnaireAnswerImage[];
}

export const ReadOnlyImageAnswer: FC<ReadOnlyImageAnswerProps> = ({
  paths,
  answerImages,
}) => {
  const byPath = useMemo(
    () => new Map((answerImages ?? []).map((image) => [image.path, image])),
    [answerImages],
  );

  const images = paths
    .map((path) => byPath.get(path))
    .filter((image): image is QuestionnaireAnswerImage => image !== undefined)
    .map((image) => ({
      // The backend only omits an entry when both URLs failed to sign, so at
      // least one of the two is always present here. When just one signed,
      // thumbnail and original fall back to the same image rather than the
      // photo vanishing from the gallery.
      imageFileUrl: image.imageUrl ?? image.resizedImageUrl ?? '',
      thumbnailImageUrl: image.resizedImageUrl ?? image.imageUrl ?? '',
    }));

  if (images.length === 0) {
    return null;
  }

  return (
    <div style={{ height: 200 }} className="w-100">
      <GalleryImages actions={{ download: true }} images={images} />
    </div>
  );
};
